<template>
  <q-card-section>
    <div id="intercom-rent-forecast-dollar" style="overflow-x: auto">
      <div style="width: 100%; height: 300px">
        <loading-overlay v-if="isLoading" />
        <line-chart v-else-if="hasChartData" :data="chartData" :options="chartOptions" />
        <slot v-else name="empty-state">
          <div
            class="full-width full-height flex flex-center text-center text-primary"
            style="font-size: 12px"
          >
            Data Required
          </div>
        </slot>
      </div>
      <q-markup-table
        v-if="hasChartData"
        dense
        bordered
        flat
        class="q-mt-md"
        separator="horizontal"
      >
        <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <tr>
            <th align="left">Forecast</th>
            <th v-for="label in chartData.labels" :key="label as string" align="center">
              {{ label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(dataset, idx) in chartData.datasets"
            :key="idx"
            align="left"
            :class="{ 'text-bold': dataset.label === 'Mid' || dataset.label === 'Rent' }"
          >
            <td>{{ dataset.label }}</td>
            <td v-for="(value, subIdx) in dataset.data" :key="subIdx" align="center">
              <q-tooltip v-if="metric === 'percent'">
                {{ getPercentChangeTooltip(value as number, subIdx) }}
              </q-tooltip>
              {{ formatValue(value as number) }}
            </td>
          </tr>
        </tbody>
      </q-markup-table>
    </div>
  </q-card-section>
</template>

<script lang="ts">
import { type PropType, computed } from 'vue'
import type { PropertyDetailsResponse } from 'common'
import { useRentProjections, useSinglePropertyRentProjection } from '@/queries/rentProjections.ts'
import LoadingOverlay from '@/components/MarketAnalytics/LoadingOverlay.vue'
import { Line as LineChart } from 'vue-chartjs'
import type { ChartData, ChartDataset, ChartOptions } from 'chart.js'
import { avg, formatMoney, formatPercent, unitTypeColor } from '@/utils'
import { format, parseISO } from 'date-fns'

export default {
  name: 'RentProjectionsChart',
  props: {
    singleProperty: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    isSingleProperty: {
      type: Boolean,
      default: false
    },
    metric: {
      type: String as PropType<'percent' | 'dollar'>,
      default: 'percent'
    },
    frequency: {
      type: String as PropType<'yearly' | 'monthly'>,
      default: 'yearly'
    }
  },
  components: {
    LoadingOverlay,
    LineChart
  },
  computed: {
    aggregatedData() {
      if (!this.rentProjections) {
        return null
      }

      if (this.frequency === 'yearly') {
        return this.aggregateToYearly(this.rentProjections)
      }

      return this.rentProjections
    },
    chartData(): ChartData<'line'> {
      if (!this.aggregatedData) {
        return { labels: [], datasets: [] }
      }

      const { lower, values, upper, dates } = this.aggregatedData
      const formatStr = this.frequency === 'yearly' ? 'yyyy' : 'MMM yyyy'
      const labels = dates.map((date) => format(parseISO(date), formatStr))

      if (this.metric === 'dollar') {
        return {
          labels,
          datasets: this.computeDataset(0, dates, lower, values, upper)
        }
      } else {
        const lowerPct: number[] = []
        const valuePct: number[] = []
        const upperPct: number[] = []
        for (let i = 1; i < values.length; i++) {
          lowerPct.push(((lower[i]! - lower[i - 1]!) / lower[i - 1]!) * 100)
          valuePct.push(((values[i]! - values[i - 1]!) / values[i - 1]!) * 100)
          upperPct.push(((upper[i]! - upper[i - 1]!) / upper[i - 1]!) * 100)
        }
        return {
          labels: labels.slice(1),
          datasets: this.computeDataset(0, dates.slice(1), lowerPct, valuePct, upperPct)
        }
      }
    },
    hasChartData() {
      return this.chartData.datasets.some((dataset) => {
        return Array.isArray(dataset.data) && dataset.data.length > 0
      })
    },
    chartOptions(): ChartOptions<'line'> {
      return {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            itemSort: (a, b) => (b.raw as number) - (a.raw as number),
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                const rawValue = context.parsed.y as number
                const val = this.formatValue(rawValue)
                return `${context.dataset.label}: ${val}`
              }
            },
            filter: (tooltipItem) => {
              if (this.frequency === 'monthly') {
                return true
              }

              if (this.chartData.labels) {
                const pointLabel = this.chartData.labels[tooltipItem.dataIndex]
                const currentYear = new Date().getFullYear()
                const pointYear = parseInt(pointLabel as string)
                const isHistoricalPoint = pointYear <= currentYear
                const primaryDatasetIndex = this.chartData.datasets.findIndex(
                  (dataset) => dataset.label === 'Mid'
                )

                if (isHistoricalPoint && tooltipItem.datasetIndex !== primaryDatasetIndex) {
                  return false
                }
              } else {
                return false
              }
              return true
            }
          },
          legend: {
            display: false
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          x: {
            grid: {
              drawTicks: false,
              drawOnChartArea: false
            },
            ticks: {
              autoSkip: true,
              maxRotation: 0,
              minRotation: 0
            }
          },
          y: {
            ticks: {
              callback: (value) => this.formatValue(value as number)
            },
            grace: '25%'
          }
        }
      }
    }
  },
  setup(props) {
    if (props.isSingleProperty) {
      const lat = computed(() => props.singleProperty?.lat ?? null)
      const lng = computed(() => props.singleProperty?.lon ?? null)
      const { rentProjections, isLoading } = useSinglePropertyRentProjection(lat, lng)
      return { rentProjections, isLoading }
    } else {
      const { rentProjections, isLoading } = useRentProjections()
      return { rentProjections, isLoading }
    }
  },
  methods: {
    formatValue(val: number | null): string {
      if (this.metric === 'dollar') {
        return formatMoney(val, 0)
      } else {
        return val !== null ? formatPercent(val / 100) : ''
      }
    },
    aggregateToYearly(data: {
      dates: string[]
      lower: number[]
      values: number[]
      upper: number[]
    }) {
      // Group data by year
      const yearGroups = data.dates.reduce(
        (groups, date, index) => {
          const year = parseISO(date).getFullYear().toString()
          const lowerValue = data.lower[index]
          const value = data.values[index]
          const upperValue = data.upper[index]

          if (lowerValue === undefined || value === undefined || upperValue === undefined) {
            return groups
          }

          if (!groups[year]) {
            groups[year] = { dates: [], lower: [], values: [], upper: [] }
          }
          groups[year].dates.push(date)
          groups[year].lower.push(lowerValue)
          groups[year].values.push(value)
          groups[year].upper.push(upperValue)
          return groups
        },
        {} as Record<
          string,
          { dates: string[]; lower: number[]; values: number[]; upper: number[] }
        >
      )

      // Calculate averages for each year and create representative dates
      const aggregatedDates: string[] = []
      const aggregatedLower: number[] = []
      const aggregatedValues: number[] = []
      const aggregatedUpper: number[] = []

      Object.values(yearGroups).forEach((group) => {
        // Use the first date of the year as representative date
        const firstDateInYear = group.dates[0]!
        aggregatedDates.push(firstDateInYear)

        // Calculate averages
        aggregatedLower.push(avg(group.lower) ?? 0)
        aggregatedValues.push(avg(group.values) ?? 0)
        aggregatedUpper.push(avg(group.upper) ?? 0)
      })

      return {
        dates: aggregatedDates,
        lower: aggregatedLower,
        values: aggregatedValues,
        upper: aggregatedUpper
      }
    },
    computeDataset(
      bed: number,
      dates: string[],
      lower: number[],
      values: number[],
      upper: number[]
    ): ChartDataset<'line'>[] {
      const today = format(new Date(), 'yyyy-MM-dd')
      const pastColor = this.$q.dark.isActive ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
      const valueColors = dates.map((date) => (date <= today ? pastColor : unitTypeColor(bed, 0.7)))

      // For monthly view with no confidence intervals, only show the main line
      if (this.frequency === 'monthly') {
        return [
          {
            label: 'Rent',
            data: values,
            borderWidth: 3,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointBackgroundColor: valueColors,
            pointBorderColor: valueColors,
            borderColor: unitTypeColor(bed, 0.7),
            backgroundColor: unitTypeColor(bed, 0.7),
            tension: 0.1,
            fill: false,
            segment: {
              borderColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7),
              backgroundColor: (ctx) => valueColors[ctx.p0DataIndex + 1] || unitTypeColor(bed, 0.7)
            }
          }
        ]
      }
      // frequency === 'yearly'
      return [
        {
          label: 'High',
          data: upper,
          borderColor: 'rgba(0, 0, 0, 0)',
          pointRadius: 0,
          fill: '+1',
          backgroundColor: unitTypeColor(bed, 0.3),
          pointHoverRadius: 3
        },
        {
          label: 'Mid',
          data: values,
          borderWidth: 3,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: valueColors,
          pointBorderColor: valueColors,
          segment: {
            borderColor: (ctx) => valueColors[ctx.p0DataIndex + 1],
            backgroundColor: (ctx) => valueColors[ctx.p0DataIndex + 1]
          }
        },
        {
          label: 'Low',
          data: lower,
          borderColor: 'rgba(0, 0, 0, 0)',
          pointRadius: 0,
          fill: '-1',
          backgroundColor: unitTypeColor(bed, 0.3),
          pointHoverRadius: 3
        }
      ]
    },
    getPreviousPeriodLabel(columnIndex: number): string {
      if (!this.aggregatedData || this.metric !== 'percent') {
        return ''
      }

      // In percent mode, chartData.labels is sliced (starts at index 1)
      const currentDateIndex = columnIndex + 1
      const previousDateIndex = columnIndex

      if (
        previousDateIndex < 0 ||
        currentDateIndex >= this.aggregatedData.dates.length ||
        !this.aggregatedData.dates[previousDateIndex]
      ) {
        return ''
      }

      const previousDate = parseISO(this.aggregatedData.dates[previousDateIndex])
      const formatStr = this.frequency === 'yearly' ? 'yyyy' : 'MMM yyyy'

      return format(previousDate, formatStr)
    },
    getPercentChangeTooltip(value: number, columnIndex: number): string {
      const previousPeriod = this.getPreviousPeriodLabel(columnIndex)
      if (!previousPeriod) {
        return ''
      }

      const absValue = Math.abs(value)
      const formattedPercent = formatPercent(absValue / 100)

      if (value >= 0) {
        return `${formattedPercent} increase from ${previousPeriod}`
      } else {
        return `${formattedPercent} decrease from ${previousPeriod}`
      }
    }
  }
}
</script>
